import React from 'react';
import styled from 'styled-components';
import { FiCpu, FiFilter, FiZap } from 'react-icons/fi';

const SelectionContainer = styled.div`
  padding: 40px 20px;
`;

const Title = styled.h2`
  text-align: center;
  color: #424242;
  margin-bottom: 10px;
  font-size: 1.8rem;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #757575;
  margin-bottom: 40px;
  font-size: 1rem;
`;

const ModesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ModeCard = styled.div`
  background: ${props => props.$selected ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff'};
  border: 3px solid ${props => props.$selected ? '#667eea' : '#e0e0e0'};
  border-radius: 16px;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(102, 126, 234, 0.3);
    border-color: #667eea;
  }
`;

const IconContainer = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${props => props.$selected ? '#fff' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${props => props.$selected ? '#667eea' : '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 20px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
`;

const ModeTitle = styled.h3`
  color: ${props => props.$selected ? '#fff' : '#424242'};
  text-align: center;
  margin-bottom: 15px;
  font-size: 1.3rem;
`;

const ModeDescription = styled.p`
  color: ${props => props.$selected ? '#e3f2fd' : '#757575'};
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const FeatureItem = styled.li`
  color: ${props => props.$selected ? '#fff' : '#616161'};
  padding: 8px 0;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:before {
    content: '✓';
    color: ${props => props.$selected ? '#4caf50' : '#667eea'};
    font-weight: bold;
    font-size: 1.2rem;
  }
`;

const SelectButton = styled.button`
  width: 100%;
  background: ${props => props.$selected ? '#fff' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${props => props.$selected ? '#667eea' : '#fff'};
  border: none;
  padding: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: #4caf50;
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ModeSelection = ({ selectedMode, onModeSelect }) => {
  const modes = [
    {
      id: 'auto',
      icon: FiCpu,
      title: 'AI Auto Processing',
      description: 'Fully automated data quality analysis with AI-powered cleansing, profiling, and duplicate detection.',
      features: [
        'Data quality analysis',
        'Duplicate detection (exact & fuzzy)',
        'Data profiling & scoring',
        'Bad data identification',
        'Comprehensive Excel report'
      ]
    },
    {
      id: 'rules',
      icon: FiFilter,
      title: 'Rules AI Only',
      description: 'Apply custom filtering rules to your raw data using natural language. Quick filtering without data quality analysis.',
      features: [
        'Natural language filtering',
        'Custom rule definition',
        'AI-powered query generation',
        'Simple filtered data export',
        'Fast processing (no analysis)'
      ]
    },
    {
      id: 'both',
      icon: FiZap,
      title: 'AI Auto + Rules',
      description: 'Complete data quality workflow. First runs automated analysis, then optionally apply custom rules for additional filtering.',
      features: [
        'Full AI auto processing',
        'Optional rules application',
        'Interactive rule definition',
        'Complete analysis + filtering',
        'Most comprehensive report'
      ]
    }
  ];

  return (
    <SelectionContainer>
      <Title>Choose Your Processing Mode</Title>
      <Subtitle>Select how you want to process your data</Subtitle>
      
      <ModesGrid>
        {modes.map(mode => {
          const Icon = mode.icon;
          const isSelected = selectedMode === mode.id;
          
          return (
            <ModeCard 
              key={mode.id}
              $selected={isSelected}
              onClick={() => onModeSelect(mode.id)}
            >
              {isSelected && <SelectedBadge>✓ Selected</SelectedBadge>}
              
              <IconContainer $selected={isSelected}>
                <Icon />
              </IconContainer>
              
              <ModeTitle $selected={isSelected}>{mode.title}</ModeTitle>
              <ModeDescription $selected={isSelected}>{mode.description}</ModeDescription>
              
              <FeatureList>
                {mode.features.map((feature, idx) => (
                  <FeatureItem key={idx} $selected={isSelected}>
                    {feature}
                  </FeatureItem>
                ))}
              </FeatureList>
              
              <SelectButton $selected={isSelected}>
                {isSelected ? 'Selected' : 'Select This Mode'}
              </SelectButton>
            </ModeCard>
          );
        })}
      </ModesGrid>
    </SelectionContainer>
  );
};

export default ModeSelection;